import { ObjectFieldModel, AllFieldTypes } from '@cak-legacy/generation-schema-core';
import { FieldFactory } from '../factory/fieldFactory';
import { Field } from '../interface/SimpleForm/Field';
import { ISimpleForm } from '../interface/SimpleForm/ISimpleForm';
import { PredefinedFieldType } from '../interface/SimpleForm/PredefinedFieldType';
import { PredefinedRegelingField } from '../interface/SimpleForm/PredefinedRegelingField';
import { Section } from '../interface/SimpleForm/Section';
import { Step } from '../interface/SimpleForm/Step';
import { IFlowSteps, IForm } from '../interface/form';
import { isAllFieldType, isRegelingFieldType } from '../utils/fieldTypeGuards';
import { nameOf } from '../utils/nameOf';

export class SimpleFormMapper {
  private fieldFactory: FieldFactory;
  /**
   *
   */
  constructor(fieldFactory: FieldFactory) {
    this.fieldFactory = fieldFactory;
  }

  mapSimpleForm = (simpleForm: ISimpleForm): IForm => {
    const flowSteps: IFlowSteps[] = simpleForm.steps.map(this.mapStep);
    const form: IForm = {
      form: {
        title: '', // This will be filled later on via the CMS
        flowSteps: flowSteps,
      },
    };
    return form;
  };

  mapStep = (step: Step): IFlowSteps => {
    const sections: { [key: string]: AllFieldTypes }[] | undefined = step.Sections?.map(this.mapSection);
    const flowStep: IFlowSteps = {
      categoryName: step.name,
      name: step.name,
      sections: sections,
      isIntroduction: step.isIntroduction,
    };
    return flowStep;
  };

  mapSection = (section: Section): { [key: string]: ObjectFieldModel } => {
    const mappedFields: [string, AllFieldTypes][] = [];
    const fieldKeys = Object.keys(section.fields);
    fieldKeys.forEach((fieldName) => {
      const field = section.fields[fieldName];
      mappedFields.push(this.mapField(fieldName, field));
    });

    const objectField: ObjectFieldModel = {
      type: 'object',
      label: section.name,
      ui: {
        flexDirection: 'column',
      },
      properties: {},
      dependsOn: section.dependsOn,
    };

    // Assign properties dynamically
    mappedFields.forEach(([fieldName, field]) => {
      objectField.properties![fieldName] = field;
    });

    return { [section.name]: objectField };
  };

  mapField = (fieldName: string, field: Field): [string, AllFieldTypes] => {
    if (isAllFieldType(field)) {
      return [fieldName, field];
    }

    switch (field.predefinedFieldType) {
      case PredefinedFieldType.achternaam:
        return [fieldName, this.fieldFactory.createAchternaamField(field)];
      case PredefinedFieldType.bijlage:
        return [fieldName, this.fieldFactory.createBijlageField(field)];
      case PredefinedFieldType.bsn:
        return [fieldName, this.fieldFactory.createBsnField(field)];
      case PredefinedFieldType.cakKenmerk:
        return [fieldName, this.fieldFactory.createKenmerkCakField(field)];
      case PredefinedFieldType.datum:
        return [fieldName, this.fieldFactory.createDatumField(field)];
      case PredefinedFieldType.email:
        return [fieldName, this.fieldFactory.createEmailField(field)];
      case PredefinedFieldType.geboortedatum:
        return [fieldName, this.fieldFactory.createGeboorteDatumField(field)];
      case PredefinedFieldType.geboortenaam:
        return [fieldName, this.fieldFactory.createGeboorteNaamField(field)];
      case PredefinedFieldType.geboorteplaats:
        return [fieldName, this.fieldFactory.createGeboortePlaatsField(field)];
      case PredefinedFieldType.geslacht:
        return [fieldName, this.fieldFactory.createGeslachtField(field)];
      case PredefinedFieldType.bedrag:
        return [fieldName, this.fieldFactory.createBedragField(field)];
      case PredefinedFieldType.huisnummer:
        return [fieldName, this.fieldFactory.createHuisnummerField(field)];
      case PredefinedFieldType.iban:
        return [fieldName, this.fieldFactory.createIbanField(field)];
      case PredefinedFieldType.adresRegel:
        return [fieldName, this.fieldFactory.createAddressLineField(field)];
      case PredefinedFieldType.straat:
        return [fieldName, this.fieldFactory.createStraatField(field)];
      case PredefinedFieldType.postcode:
        return [fieldName, this.fieldFactory.createPostcodeField(field)];
      case PredefinedFieldType.plaats:
        return [fieldName, this.fieldFactory.createPlaatsField(field)];
      case PredefinedFieldType.provincie:
        return [fieldName, this.fieldFactory.createProvincieField(field)];
      case PredefinedFieldType.land:
        return [fieldName, this.fieldFactory.createLandField(field)];
      case PredefinedFieldType.radioYesNo:
        return [fieldName, this.fieldFactory.createRadioYesNoField(field)];
      case PredefinedFieldType.regeling:
        if (isRegelingFieldType(field)) {
          return [fieldName, this.fieldFactory.createRegelingField(field)];
        } else {
          throw Error(
            `regeling field is not of the right interface. You're likely missing ${
              nameOf<PredefinedRegelingField>().regelingOptions
            }`
          );
        }
      case PredefinedFieldType.telefoonnummer:
        return [fieldName, this.fieldFactory.createTelefoonnummerField(field)];
      case PredefinedFieldType.text:
        return [fieldName, this.fieldFactory.createTextField(field)];
      case PredefinedFieldType.textArea:
        return [fieldName, this.fieldFactory.createTextAreaField(field)];
      case PredefinedFieldType.voorletters:
        return [fieldName, this.fieldFactory.createVoorlettersField(field)];
      case PredefinedFieldType.voornaam:
        return [fieldName, this.fieldFactory.createVoornaamField(field)];
      default:
        throw Error(`unsupported field type: ${field.predefinedFieldType}`);
    }
  };
}
